/**
 * Trilingual UI strings for share flows in the legacy `frontend/` app.
 */
export type ShareLocale = "en-US" | "ja-JP" | "ko-KR";

const STRINGS: Record<
  ShareLocale,
  {
    shareMenuTitle: string;
    copyLink: string;
    linkCopied: string;
    shareTo: string;
    shareVia: string;
    sendToChat: string;
    shareToChatTitle: string;
    shareToChatDescription: string;
    recentConversations: string;
    allConversations: string;
    searchConversationsPlaceholder: string;
    selectedConversations: string;
    optionalMessageLabel: string;
    optionalMessagePlaceholder: string;
    noConversationsTitle: string;
    noConversationsSubtitle: string;
    noSearchResultsTitle: string;
    noSearchResultsSubtitle: string;
    send: string;
    sending: string;
    sharedSuccessSingle: string;
    sharedSuccessMulti: string;
    sharedPartial: string;
    shareFailed: string;
    sharedContentLabel: string;
    viewSharedContent: string;
    openInApp: string;
    openInNewTab: string;
    closeViewer: string;
    backToChat: string;
    loadingContent: string;
    contentUnavailable: string;
  }
> = {
  "en-US": {
    shareMenuTitle: "Share",
    copyLink: "Copy link",
    linkCopied: "Link copied!",
    shareTo: "Share to…",
    shareVia: "Share via",
    sendToChat: "Send to Chat",
    shareToChatTitle: "Send to chat",
    shareToChatDescription: "Choose one or more conversations and optionally add a note.",
    recentConversations: "Recent",
    allConversations: "Conversations",
    searchConversationsPlaceholder: "Search conversations",
    selectedConversations: "Selected",
    optionalMessageLabel: "Add a note (optional)",
    optionalMessagePlaceholder: "Say something about this share…",
    noConversationsTitle: "No conversations yet",
    noConversationsSubtitle: "Start a chat first, then send shared content here.",
    noSearchResultsTitle: "No matching conversations",
    noSearchResultsSubtitle: "Try a different name or open a recent chat instead.",
    send: "Send",
    sending: "Sending…",
    sharedSuccessSingle: "Shared to chat.",
    sharedSuccessMulti: "Shared to {count} chats.",
    sharedPartial: "Shared to {count} chats. Some deliveries failed.",
    shareFailed: "We could not share this to chat right now.",
    sharedContentLabel: "Shared content",
    viewSharedContent: "View shared content",
    openInApp: "Open in app",
    openInNewTab: "Open in new tab",
    closeViewer: "Close viewer",
    backToChat: "Back to chat",
    loadingContent: "Loading shared content…",
    contentUnavailable: "This shared content is unavailable right now.",
  },
  "ja-JP": {
    shareMenuTitle: "共有",
    copyLink: "リンクをコピー",
    linkCopied: "リンクをコピーしました！",
    shareTo: "共有先…",
    shareVia: "共有方法",
    sendToChat: "チャットで送信",
    shareToChatTitle: "チャットで送信",
    shareToChatDescription: "1つ以上の会話を選び、必要ならメモを追加できます。",
    recentConversations: "最近",
    allConversations: "会話",
    searchConversationsPlaceholder: "会話を検索",
    selectedConversations: "選択中",
    optionalMessageLabel: "メモを追加（任意）",
    optionalMessagePlaceholder: "この共有についてひと言…",
    noConversationsTitle: "会話がまだありません",
    noConversationsSubtitle: "先にチャットを始めると、ここに共有できます。",
    noSearchResultsTitle: "一致する会話がありません",
    noSearchResultsSubtitle: "別の名前で検索するか、最近のチャットを開いてください。",
    send: "送信",
    sending: "送信中…",
    sharedSuccessSingle: "チャットに共有しました。",
    sharedSuccessMulti: "{count} 件のチャットに共有しました。",
    sharedPartial: "{count} 件のチャットに共有しました。一部は送信できませんでした。",
    shareFailed: "今はチャットに共有できませんでした。",
    sharedContentLabel: "共有コンテンツ",
    viewSharedContent: "共有コンテンツを見る",
    openInApp: "アプリで開く",
    openInNewTab: "新しいタブで開く",
    closeViewer: "ビューアを閉じる",
    backToChat: "チャットに戻る",
    loadingContent: "共有コンテンツを読み込み中…",
    contentUnavailable: "この共有コンテンツは現在利用できません。",
  },
  "ko-KR": {
    shareMenuTitle: "공유",
    copyLink: "링크 복사",
    linkCopied: "링크를 복사했습니다!",
    shareTo: "공유 대상…",
    shareVia: "공유 방법",
    sendToChat: "채팅으로 보내기",
    shareToChatTitle: "채팅으로 보내기",
    shareToChatDescription: "하나 이상의 대화를 선택하고 메모를 추가할 수 있습니다.",
    recentConversations: "최근",
    allConversations: "대화",
    searchConversationsPlaceholder: "대화 검색",
    selectedConversations: "선택됨",
    optionalMessageLabel: "메모 추가(선택)",
    optionalMessagePlaceholder: "이 공유에 대해 한마디…",
    noConversationsTitle: "아직 대화가 없습니다",
    noConversationsSubtitle: "먼저 채팅을 시작하면 여기로 공유할 수 있습니다.",
    noSearchResultsTitle: "일치하는 대화가 없습니다",
    noSearchResultsSubtitle: "다른 이름으로 검색하거나 최근 채팅을 열어 보세요.",
    send: "보내기",
    sending: "보내는 중…",
    sharedSuccessSingle: "채팅으로 공유했습니다.",
    sharedSuccessMulti: "{count}개의 채팅에 공유했습니다.",
    sharedPartial: "{count}개의 채팅에 공유했습니다. 일부 전송은 실패했습니다.",
    shareFailed: "지금은 채팅으로 공유할 수 없습니다.",
    sharedContentLabel: "공유 콘텐츠",
    viewSharedContent: "공유 콘텐츠 보기",
    openInApp: "앱에서 열기",
    openInNewTab: "새 탭에서 열기",
    closeViewer: "뷰어 닫기",
    backToChat: "채팅으로 돌아가기",
    loadingContent: "공유 콘텐츠를 불러오는 중…",
    contentUnavailable: "이 공유 콘텐츠는 현재 사용할 수 없습니다.",
  },
};

/**
 * Resolve a supported browser locale for share UI.
 */
export function resolveShareLocale(): ShareLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }

  const language = navigator.language.toLowerCase();
  if (language.startsWith("ja")) {
    return "ja-JP";
  }
  if (language.startsWith("ko")) {
    return "ko-KR";
  }

  return "en-US";
}

/**
 * Return share UI strings for the requested or detected locale.
 */
export function getShareUiStrings(locale?: ShareLocale): (typeof STRINGS)["en-US"] {
  const resolvedLocale = locale ?? resolveShareLocale();
  return STRINGS[resolvedLocale] ?? STRINGS["en-US"];
}
